import { useColorModeValue } from '@chakra-ui/react'
import {
  ContentNodeUtils,
  type DataTypeConfig,
  DataTypeConfigUtils,
  type DataTypeLocator,
  type DateFormat,
  type DateParts,
} from '@helo/cms'
import { pipe } from '@helo/utils'
import { isValid, parseISO } from 'date-fns'

import { toWallClockDateString } from '../../../utils/dates'
import { type DateGranularity, Format } from '../../Format'
import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartCartesianOutput } from '../ChartCartesian'
import type { ChartDataOutput } from '../ChartData'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import type { ChartAxisSlice } from './ChartAxis'
import { ChartAxisConstants } from './ChartAxis.constants'
import {
  type AxisTickLabelLayout,
  type AxisTickLabelOrientation,
  type ChartAxisResolvedOptions,
  chartAxisOptions,
} from './ChartAxis.options'

const YMD_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

const parseCategoryDate = (raw: string): Date | undefined => {
  const value = String(raw).trim()
  if (!value) return undefined

  if (value.includes('T')) {
    const parsed = parseISO(value)
    return isValid(parsed) ? parsed : undefined
  }

  const ymd = YMD_ONLY.exec(value)
  if (ymd) {
    const parsed = parseISO(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00.000Z`)
    return isValid(parsed) ? parsed : undefined
  }

  return undefined
}

export type TickLabelFormatState = {
  dateFormat?: string
  dateGranularity?: DateGranularity
}

export type ChartAxisDateFormatOptions = {
  isDate?: boolean
  formatState?: TickLabelFormatState
}

export type TickLabelKind = 'date' | 'number' | 'text'

export type ResolveTickLabelLinesInput = {
  raw: string
  kind: TickLabelKind
  formatState?: TickLabelFormatState
  layout?: AxisTickLabelLayout
  truncation?: number
}

const asFormatState = (state?: TickLabelFormatState) =>
  (state ?? {}) as Parameters<typeof Format.date>[0]

const formatTickLabel = (
  raw: string,
  { isDate, formatState }: ChartAxisDateFormatOptions,
): string => {
  if (!isDate) return raw
  const wallClock = toWallClockDateString(raw)
  return Format.date(asFormatState(formatState))(wallClock)
}

const resolveTickLabelOrientation = (
  layout: AxisTickLabelLayout | undefined,
): AxisTickLabelOrientation => {
  switch (layout) {
    case 'diagonal':
    case 'diagonal-stacked':
      return 'diagonal'
    case 'vertical':
    case 'vertical-stacked':
      return 'vertical'
    default:
      return 'horizontal'
  }
}

const truncateTickLabel = (
  text: string,
  limit = ChartAxisConstants.defaultAxisTickLabelTruncation,
): string => {
  if (text.length <= limit) return text
  return `${text.slice(0, Math.max(0, limit - 3))}...`
}

const resolveDateFormat = (
  formatState: TickLabelFormatState | undefined,
): 'iso' | 'short' | 'medium' | 'long' => {
  const format = formatState?.dateFormat
  if (
    format === 'iso' ||
    format === 'short' ||
    format === 'medium' ||
    format === 'long'
  ) {
    return format
  }
  return 'medium'
}

const resolveDateGranularity = (
  formatState: TickLabelFormatState | undefined,
): DateGranularity => {
  const value = formatState?.dateGranularity
  if (
    value === 'second' ||
    value === 'minute' ||
    value === 'hour' ||
    value === 'day' ||
    value === 'week' ||
    value === 'month' ||
    value === 'year'
  ) {
    return value
  }
  return 'day'
}

const isoWeek = (parts: DateParts): { week: number; weekYear: number } => {
  const date = new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
  )
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const weekYear = date.getUTCFullYear()
  const yearStart = new Date(Date.UTC(weekYear, 0, 1))
  const week = Math.ceil(
    ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  )
  return { week, weekYear }
}

const stackDateTickLines = (
  parts: DateParts,
  granularity: DateGranularity,
  format: DateFormat,
): string[] => {
  if (granularity === 'year') return [parts.year]

  if (granularity === 'week') {
    const { week, weekYear } = isoWeek(parts)
    if (format === 'iso') {
      return [`W${String(week).padStart(2, '0')}`, String(weekYear)]
    }
    return [`Week ${week}`, String(weekYear)]
  }

  if (granularity === 'month') {
    if (format === 'iso') return [parts.monthPad, parts.year]
    if (format === 'short') return [parts.month, parts.year]
    if (format === 'long') return [parts.monthLong, parts.year]
    return [parts.monthShort, parts.year]
  }

  const dayMonth =
    format === 'iso'
      ? `${parts.monthPad}-${parts.dayPad}`
      : format === 'short'
        ? `${parts.month}/${parts.day}`
        : format === 'long'
          ? `${parts.day} ${parts.monthLong}`
          : `${parts.day} ${parts.monthShort}`

  if (granularity === 'day') return [dayMonth, parts.year]

  const time =
    granularity === 'hour'
      ? `${parts.hourPad}:00`
      : granularity === 'minute'
        ? `${parts.hourPad}:${parts.minutePad}`
        : `${parts.hourPad}:${parts.minutePad}:${parts.secondPad}`
  return [`${dayMonth}, ${time}`, parts.year]
}

const resolveStackedDateLines = (
  raw: string,
  formatState: TickLabelFormatState | undefined,
): string[] => {
  const wallClock = toWallClockDateString(raw)
  if (typeof wallClock !== 'string')
    return [formatTickLabel(raw, { isDate: true, formatState })]
  const parts = Format.dateParts(asFormatState(formatState))(wallClock)
  if (parts == null) {
    return [formatTickLabel(raw, { isDate: true, formatState })]
  }
  return stackDateTickLines(
    parts,
    resolveDateGranularity(formatState),
    resolveDateFormat(formatState),
  )
}

/**
 * Layout switch for stacked tick labels. Diagonal/vertical stacked variants
 * currently match their non-stacked counterparts; add stacking here later.
 */
const resolveTickLabelLines = ({
  raw,
  kind,
  formatState,
  layout = ChartAxisConstants.defaultAxisTickLabelLayout,
  truncation = ChartAxisConstants.defaultAxisTickLabelTruncation,
}: ResolveTickLabelLinesInput): string[] => {
  const formatted =
    kind === 'date' ? formatTickLabel(raw, { isDate: true, formatState }) : raw

  switch (layout) {
    case 'horizontal-stacked': {
      if (kind === 'number') return [truncateTickLabel(formatted, truncation)]
      if (kind === 'date') {
        return resolveStackedDateLines(raw, formatState).map(line =>
          truncateTickLabel(line, truncation),
        )
      }
      return formatted
        .split('\n')
        .filter(Boolean)
        .map(line => truncateTickLabel(line, truncation))
    }
    case 'diagonal-stacked':
    case 'vertical-stacked':
    case 'diagonal':
    case 'vertical':
    case 'horizontal':
    default:
      return [truncateTickLabel(formatted, truncation)]
  }
}

const domainLooksLikeDates = (values: string[]): boolean => {
  const candidates = values.map(value => value.trim()).filter(Boolean)
  return (
    candidates.length > 0 &&
    candidates.every(value => parseCategoryDate(value) != null)
  )
}

const tickLineCount = (
  values: string[],
  options: Omit<ResolveTickLabelLinesInput, 'raw'>,
): number => {
  if (values.length === 0) return 1
  return Math.max(
    1,
    ...values.map(
      value => resolveTickLabelLines({ ...options, raw: value }).length,
    ),
  )
}

const isDateTypeName = (typeName: string | undefined) =>
  typeName === 'Date' || typeName === 'DateTime'

const isDateField = (root: DataTypeConfig, locator: DataTypeLocator) => {
  const field = ChartDataParts.findField(locator)(root)
  const ofType = DataTypeConfigUtils.getOfType()(field)
  if (isDateTypeName(ofType?.typeName) || isDateTypeName(field.typeName)) {
    return true
  }
  if (locator.length < 2) return false
  const parent = DataTypeConfigUtils.find(locator.slice(0, -1))(root)
  if (parent == null || !DataTypeConfigUtils.isKind('FIELD')(parent)) {
    return false
  }
  return (
    DataTypeConfigUtils.getOfType()(parent)?.typeName === 'CubeTimeDimension'
  )
}

/**
 * VisX AxisBottom places the title at
 * `tickLength + labelOffset + tickFontSize + labelFontSize` (alphabetic baseline).
 * Pass only the gaps VisX does not already add (tick dy + title gap).
 */
const xLabelOffset = (): number =>
  ChartAxisConstants.xTickMargin + ChartAxisConstants.xLabelGap

/**
 * VisX AxisLeft places the title at `tickLength + labelOffset` from the axis
 * line and does not add tick-label width. Subtract the rotated title thickness
 * (font size) so the title sits in the left padding with ~yLabelGap to ticks.
 */
const yLabelOffset = (paddingLeft: number): number =>
  Math.max(
    ChartAxisConstants.yTickMargin + ChartAxisConstants.yLabelGap,
    paddingLeft - ChartAxisConstants.axisLabel.fontSize,
  )

const xTickTextAnchor = (
  index: number,
  count: number,
): 'start' | 'middle' | 'end' => {
  if (count <= 1) return 'middle'
  if (index === 0) return 'start'
  if (index === count - 1) return 'end'
  return 'middle'
}

const useAxisStroke = () =>
  useColorModeValue(
    ChartAxisConstants.axisStroke.light,
    ChartAxisConstants.axisStroke.dark,
  )

const useTickLabelFill = () =>
  useColorModeValue(
    ChartAxisConstants.tickLabel.fill,
    ChartAxisConstants.tickLabelFillDark,
  )

const useAxisLabelFill = () =>
  useColorModeValue(
    ChartAxisConstants.axisLabel.fill,
    ChartAxisConstants.axisLabelFillDark,
  )

type ChartAxisSetupOutput = ChartDataOutput &
  ChartCartesianOutput &
  Pick<QueryOutput, 'contentNode'> & { axis?: ChartAxisSlice }

/** Write tick labels, date detection, and axis chrome from resolved input. */
const setAxis = () =>
  RenderPipeline.updateOutput(
    (
      output: ChartAxisSetupOutput,
      ctx: {
        input: {
          _xAxis?: ChartAxisResolvedOptions
          _yAxis?: ChartAxisResolvedOptions
        }
      },
    ) => {
      const x = (ctx.input._xAxis ?? {}) as ChartAxisResolvedOptions
      const y = (ctx.input._yAxis ?? {}) as ChartAxisResolvedOptions
      const listContentNode = ChartDataParts.getListContentNode(
        output.contentNode!,
      )
      const rootDataTypeConfig = pipe(
        listContentNode,
        ContentNodeUtils.getDataTypeConfig(),
      )
      const xAxisIsDate =
        isDateField(rootDataTypeConfig, output.data.dimensionLocator) ||
        domainLooksLikeDates(output.cartesian.xDomain)
      const yTicks = y.ticks ?? output.cartesian.yTicks
      const isHorizontal = output.cartesian.plotOrientation === 'horizontal'
      const categoryKind = xAxisIsDate ? ('date' as const) : ('text' as const)
      const xTickOptions = {
        kind: isHorizontal ? ('number' as const) : categoryKind,
        formatState: x,
        layout: x.axisTickLabelLayout,
        truncation: x.axisTickLabelTruncation,
      }
      const yTickOptions = {
        kind: isHorizontal ? categoryKind : ('number' as const),
        formatState: y,
        layout: y.axisTickLabelLayout,
        truncation: y.axisTickLabelTruncation,
      }
      const xValues = isHorizontal
        ? yTicks.map(String)
        : output.cartesian.xDomain
      const yValues = isHorizontal
        ? output.cartesian.xDomain
        : yTicks.map(String)
      const measureLabel = (
        raw: string,
        options: typeof xTickOptions,
      ): string =>
        resolveTickLabelLines({
          ...options,
          layout: 'horizontal',
          raw,
        })[0] ?? ''

      return {
        axis: {
          x: {
            ...x,
            showTicks: x.showTicks,
            showLine: x.showLine,
          },
          y: {
            ...y,
            showTicks: y.showTicks,
            showLine: y.showLine,
            ticks: yTicks,
          },
          xAxisIsDate,
          xTickLineCount: tickLineCount(xValues, xTickOptions),
          xTickLabelOrientation: resolveTickLabelOrientation(
            x.axisTickLabelLayout,
          ),
          yTickLabelOrientation: resolveTickLabelOrientation(
            y.axisTickLabelLayout,
          ),
          xTickLabels: xValues.map(value => measureLabel(value, xTickOptions)),
          yTickLabels: yValues.map(value => measureLabel(value, yTickOptions)),
        },
      }
    },
  )

/** Resolve axis options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(chartAxisOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(chartAxisOptions),
      Options.validate(chartAxisOptions),
    ),
  )

/** Same resolution for `_xAxis`. */
const setup_xAxisInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _xAxis: pipe(
      input._xAxis ?? {},
      Options.fallback(
        chartAxisOptions,
        ChartDataParts.getNestedState('_xAxis')(ctx),
      ),
      Options.applyDefaults(chartAxisOptions),
    ),
  }))

/** Same resolution for `_yAxis`. */
const setup_yAxisInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _yAxis: pipe(
      input._yAxis ?? {},
      Options.fallback(
        chartAxisOptions,
        ChartDataParts.getNestedState('_yAxis')(ctx),
      ),
      Options.applyDefaults(chartAxisOptions),
    ),
  }))

export const ChartAxisInputParts = {
  setupInput,
  setup_xAxisInput,
  setup_yAxisInput,
}

export const ChartAxisOutputParts = {
  setAxis,
}

export const ChartAxisParts = {
  isDateField,
  xLabelOffset,
  yLabelOffset,
  xTickTextAnchor,
  useAxisStroke,
  useTickLabelFill,
  useAxisLabelFill,
  formatTickLabel,
  domainLooksLikeDates,
  truncateTickLabel,
  resolveTickLabelLines,
  resolveTickLabelOrientation,
  tickLineCount,
  parseCategoryDate,
  yTickDx: -ChartAxisConstants.yTickMargin,
  xTickDy: ChartAxisConstants.xTickMargin,
  dataLabelOffsetY: ChartAxisConstants.dataLabelOffsetY,
  dataLabelOffsetX: ChartAxisConstants.dataLabelOffsetX,
}

export const ChartAxisUtils = ChartAxisParts
